import { FeatureFlag } from '@ps/web-core-enums';
import { Link, Translation } from '@ps/web-shared-components';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store/types';
import { Icon } from '@swf/sports/components';
import getWindow from '@swf/sports/utils/getWindow';

import formatExpiresTime from '../../utils/formatExpiresTime';

import { TXTCLI_Completed, TXTWCL_Read_More } from './IcePromotion.messages.json';
import styles from './IcePromotion.scss';
import type { IcePromotion as IcePromotionType } from './types';

interface Props {
  promotion: IcePromotionType;
}

const isPromotionCompleted = ({ tasks, userTask }: IcePromotionType) => {
  const [{ taskState }] = tasks;

  return (userTask ? userTask.userTaskState : taskState) === 'COMPLETED';
};

const getPromotionPercentageCompleted = (promotion: IcePromotionType): number | null => {
  const { userTask } = promotion;

  if (isPromotionCompleted(promotion)) return 100;

  if (!userTask) return null;

  if (!userTask.userTaskCounterTotalValue || !userTask.userTaskCounterMaxValue) return 0;

  return (100 * userTask.userTaskCounterTotalValue) / userTask.userTaskCounterMaxValue;
};

const IcePromotion: React.FC<Props> = ({ promotion }) => {
  const {
    progressAvailable,
    promoId,
    promoBranding: image,
    promoTitle,
    promoDescription
  } = promotion;

  const { modal } = useDispatch<Dispatch>();
  const userCurrency = useSelector(select.account.getCurrency);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const locale = useSelector(select.locale.get);
  const isPRHubEnabled = useFeature(FeatureFlag.PRHub);
  const isCompleted = isPromotionCompleted(promotion);

  // title and description may contain placeholders for currency symbol
  const { title, description } = useMemo(() => {
    if (!userCurrency) return { title: promoTitle, description: promoDescription };
    const symbol = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: userCurrency
    })
      .formatToParts(1)
      .find(({ type }) => type === 'currency')?.value;

    const placeholderMatcher = /{PrefCur}|{PrefCurAccept}/gi;
    return {
      title: promoTitle.replace(placeholderMatcher, symbol ?? ''),
      description: (promoDescription ?? '').replace(placeholderMatcher, symbol ?? '')
    };
  }, [promoTitle, promoDescription, locale, userCurrency]);

  const percentageCompleted = getPromotionPercentageCompleted(promotion);
  const [{ expiresTime }] = promotion.tasks;

  const formattedExpiresTime = expiresTime ? formatExpiresTime(expiresTime) : null;

  const linkHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    if (isEmbedded && isPRHubEnabled) {
      getWindow()?.starsweb?.app.execute(`ice/open?promoid=${promoId}&trigger=info-popup`);
    } else {
      modal.enqueue({
        path: `/account/ice/challenges/${promoId}/info-popup`
      });
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.banner}>
        <img
          src={`https://s1.rationalcdn.com/vendors/pyr/ice/images/promocover_${image}_isp.png`}
          alt=""
        />
      </div>

      <div className={styles.body}>
        <div>
          <div className={styles.divideY}>
            {progressAvailable && percentageCompleted !== null ? (
              <progress value={percentageCompleted} max={100} className={styles.progressBar} />
            ) : null}

            <h2 className={styles.title}>
              <Link
                className={styles.link}
                onClick={linkHandler}
                href={`/account/ice/challenges/${promoId}/info-popup`}
              >
                {title}
              </Link>
            </h2>
          </div>

          <p className={styles.description}>{description}</p>
        </div>
        <div className={styles.actionBar}>
          {isCompleted ? (
            <div className={styles.completed} aria-hidden="true">
              <Icon size="lg" name="tick-medium" />
              <Translation {...TXTCLI_Completed} />
            </div>
          ) : (
            <div className={styles.countdown}>
              <Icon size="xl" name="time" />
              {formattedExpiresTime}
            </div>
          )}
          <div className={styles.readMore} role="link">
            <Translation {...TXTWCL_Read_More} />
            <Icon size="lg" name="chevron-medium-right" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default IcePromotion;
